import { useCallback, useEffect, useState } from "react";
import styles from "./InstallBanner.module.css";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // Safari iOS
    ("standalone" in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

export function InstallBanner() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    if (isIos()) {
      setShowIosHint(true);
      return;
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === "accepted") {
      setPromptEvent(null);
    }
    setDismissed(true);
  }, [promptEvent]);

  if (isStandalone() || dismissed) return null;

  if (showIosHint) {
    return (
      <div className={styles.banner} role="note">
        <span className={styles.text}>
          Install: tap <strong>Share</strong> then <strong>Add to Home Screen</strong>
        </span>
        <button type="button" className={styles.dismiss} onClick={() => setDismissed(true)} aria-label="Dismiss">
          ×
        </button>
      </div>
    );
  }

  if (!promptEvent) return null;

  return (
    <div className={styles.banner}>
      <span className={styles.text}>Install A.A.R.I.A. for quick fleet access</span>
      <div className={styles.actions}>
        <button type="button" className={styles.install} onClick={() => void install()}>
          Install
        </button>
        <button type="button" className={styles.dismiss} onClick={() => setDismissed(true)} aria-label="Dismiss">
          ×
        </button>
      </div>
    </div>
  );
}
